import React, {useEffect, useState, useCallback} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';
import { processWechatAuth, cleanupAuthData } from '../utils/wechatAuthManager';
import { buildAuthRedirectUrl, executeAuthRedirect } from '../utils/authRedirectManager';

export default function WechatCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [debugInfo, setDebugInfo] = useState('');

    const handleWechatCallback = useCallback(async (code, formId, targetUrl) => {
        const state = searchParams.get('state');
        console.log('WechatCallback: 开始处理授权回调', {code, formId, state, targetUrl});

        try {
            // 使用新的授权管理器处理授权
            const success = await processWechatAuth(code, formId, state);
            
            if (success) {
                console.log('WechatCallback: 授权成功，准备跳转');
                console.log('WechatCallback: 使用传入的targetUrl:', targetUrl);
                
                // 延迟清理授权数据，确保跳转完成
                setTimeout(() => {
                    cleanupAuthData();
                }, 1000);
                
                // 直接使用传入的targetUrl进行跳转
                console.log('WechatCallback: 跳转到:', targetUrl);
                navigate(targetUrl, { replace: true });
            } else {
                throw new Error('微信授权处理失败');
            }
        } catch (error) {
            console.error('WechatCallback: 微信授权回调处理失败:', error);
            
            const errorInfo = {
                message: error.message,
                stack: error.stack,
                timestamp: new Date().toISOString(),
                url: window.location.href,
                userAgent: navigator.userAgent,
                searchParams: Object.fromEntries(searchParams.entries()),
                localStorage: {
                    wechatUser: localStorage.getItem('wechatUser'),
                    returnUrl: localStorage.getItem('returnUrl'),
                    isProcessingWechatAuth: localStorage.getItem('isProcessingWechatAuth')
                }
            };
            
            setError('微信授权失败，请联系管理员或稍后重试。\n\n错误详情: ' + error.message);
            setDebugInfo(JSON.stringify(errorInfo, null, 2));
            setLoading(false);
        }
    }, [searchParams, navigate]);

    useEffect(() => {
        console.log('WechatCallback: 组件开始初始化');

        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const formId = searchParams.get('formId') || localStorage.getItem('wechatFormId');

        console.log('WechatCallback: 授权参数', {code, state, formId});

        if (!code || (state !== 'wechat_auth' && state !== 'blackcard' && !state?.startsWith('sign_in_sheet:') && !state?.startsWith('detail/'))) {
            console.log('WechatCallback: 授权参数错误');
            setError('授权参数错误');
            setLoading(false);
            return;
        }

        // 直接处理授权回调，不添加延时
        console.log('WechatCallback: 开始处理授权回调');
        console.log('📝 当前state参数:', state);
        
        // 根据state参数决定跳转逻辑
        let targetUrl;
        if (state === 'wechat_auth') {
            targetUrl = '/forms';
            console.log('🔄 默认授权，跳转到表单列表');
        } else if (state === 'blackcard') {
            targetUrl = '/black-card-activation-mobile';
            console.log('🔄 黑卡授权，跳转到黑卡页面');
        } else if (state && state.startsWith('sign_in_sheet:')) {
            // 签到场景：从state中解析brand参数值，构建完整URL
            // state格式：sign_in_sheet:%E6%B5%8B%E8%AF%953（只保存brand参数的值）
            const brandValue = state.replace('sign_in_sheet:', ''); // 提取brand参数值
            // 构建返回路径：/sign-in-sheet-form?brand=参数值
            targetUrl = brandValue ? `/sign-in-sheet-form?brand=${encodeURIComponent(brandValue)}` : '/sign-in-sheet-form';
            console.log('🔄 签到授权，从state解析brand参数跳转到签到页面:', targetUrl);

        } else if (state && state.startsWith('detail/')) {
            const formId = state.replace('detail/', '');
            targetUrl = `/display/${formId}`;
            console.log('🔄 表单详情页授权，跳转到display页面:', targetUrl);
        } else {
            targetUrl = '/forms';
            console.log('🔄 其他情况，使用默认跳转');
        }
        
        // 处理微信授权回调
        handleWechatCallback(code, formId, targetUrl);
    }, [searchParams, handleWechatCallback]);

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: '#f5f7fa'
            }}>
                <div style={{
                    width: 40,
                    height: 40,
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #1890ff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{marginTop: 16, color: '#666'}}>正在处理微信授权...</p>
                <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: '#f5f7fa',
                padding: '20px'
            }}>
                <div style={{color: 'red', fontSize: '18px', marginBottom: '16px', textAlign: 'center'}}>
                    {error}
                </div>
                
                {/* 调试信息显示 */}
                {debugInfo && (
                    <div style={{
                        width: '100%',
                        maxWidth: '800px',
                        marginBottom: '16px',
                        padding: '16px',
                        background: '#f0f0f0',
                        borderRadius: '8px',
                        border: '1px solid #d9d9d9'
                    }}>
                        <h4 style={{margin: '0 0 12px 0', color: '#333'}}>WechatCallback 调试信息:</h4>
                        <pre style={{
                            fontSize: '12px',
                            color: '#666',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            margin: 0,
                            maxHeight: '300px',
                            overflow: 'auto'
                        }}>
                            {debugInfo}
                        </pre>
                    </div>
                )}
                
                <div style={{display: 'flex', gap: '12px'}}>
                    <button
                        onClick={() => window.history.back()}
                        style={{
                            padding: '8px 16px',
                            background: '#1890ff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        返回
                    </button>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '8px 16px',
                            background: '#52c41a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        刷新页面
                    </button>
                    {debugInfo && (
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(debugInfo).then(() => {
                                    alert('调试信息已复制到剪贴板');
                                });
                            }}
                            style={{
                                padding: '8px 16px',
                                background: '#722ed1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            复制调试信息
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return null;
} 